import { TenantFeatureToggle, TenantType } from '@xeetr/types/enums'
import { BookingCustomField, BookingCustomFieldType, Settings } from '@xeetr/types/models'
import { Plus } from 'lucide-react'
import React, { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import FloatingLabel from '../../../inputs/FloatingLabel'
import LabeledInputNumber from '../../../inputs/LabeledInputNumber'
import LabeledInputSwitch from '../../../inputs/LabeledInputSwitch'

import FoodTenantReservation from './TenantReservation'
import ServiceTenantReservation from './TenantReservationDefaultState'
import SortableCustomFieldContainer from './SortableCustomFieldContainer'
import { SortableCustomField } from './SortableCustomField'

import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useConfirmDialog } from '@/hooks/common/useConfirmDialog'
import useFeatureToggle from '@/hooks/common/useFeatureToggle'
import { useTenant } from '@/hooks/useTenant'
import { TenantCustomCondition } from './TenantCustomCondition'

/**
 * Migrates custom fields that don't have IDs by generating UUIDs for them.
 * This ensures backward compatibility with existing fields.
 */
const migrateCustomFieldsWithIds = (fields: BookingCustomField[]): BookingCustomField[] => {
  return fields.map((field) => {
    if (!field.id) {
      return {
        ...field,
        id: uuidv4(),
      }
    }
    return field
  })
}

const TenantReservation = () => {
  const { control, setValue, getValues } = useFormContext<Settings>()
  const hasCustomerAddress = useFeatureToggle(TenantFeatureToggle.CUSTOMER_ADDRESS)

  const { tenant } = useTenant()

  const [newFieldName, setNewFieldName] = useState('')
  const [newFieldDescription, setNewFieldDescription] = useState('')
  const [newFieldRequired, setNewFieldRequired] = useState(false)
  const [newFieldHidden, setNewFieldHidden] = useState(false)
  const [newFieldType, setNewFieldType] = useState<BookingCustomFieldType>('text')
  const [newFieldOptions, setNewFieldOptions] = useState('')
  const [newFieldCustomerVisible, setNewFieldCustomerVisible] = useState(true)

  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null)

  // use a custom keyName so react-hook-form doesn't override our
  // business `id` property.  the default `id` field is reserved for its
  // internal tracking key, which changes on every render and was the
  // source of confusion/bug reports.
  const { fields, append, remove, update, move } = useFieldArray({
    control,
    name: 'tenant.settings.bookingCustomFields',
    keyName: 'fieldArrayId',
  })

  // Apply migration on mount if needed
  React.useEffect(() => {
    const currentFields = getValues('tenant.settings.bookingCustomFields')
    if (currentFields && currentFields.length > 0) {
      const needsMigration = currentFields.some((field: BookingCustomField) => !field.id)
      if (needsMigration) {
        const migratedFields = migrateCustomFieldsWithIds(currentFields)
        // Use setValue with shouldDirty to mark form as dirty so changes are saved
        setValue('tenant.settings.bookingCustomFields', migratedFields, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    }
  }, []) // Run only once on mount

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      // because we supply a custom keyName, our tracking id lives under
      // `fieldArrayId` rather than `id`.
      const oldIndex = fields.findIndex((field) => (field as any).fieldArrayId === active.id)
      const newIndex = fields.findIndex((field) => (field as any).fieldArrayId === over.id)

      move(oldIndex, newIndex)
    }
  }

  const confirmDialog = useConfirmDialog()

  const handleAddField = () => {
    if (!newFieldName.trim() || !newFieldDescription.trim()) return
    if (newFieldType === 'enum' && !newFieldOptions.trim()) return

    const isDuplicate = fields.some(
      (field) => field.name.toLowerCase() === newFieldName.trim().toLowerCase()
    )
    if (isDuplicate) {
      confirmDialog.confirm(
        {
          title: 'Doppelter Feldname',
          description: `Ein Feld mit dem Namen "${newFieldName.trim()}" existiert bereits. Bitte verwenden Sie einen anderen Namen.`,
          confirmText: 'OK',
          cancelText: '',
          variant: 'default',
        },
        () => {}
      )
      return
    }

    append({
      id: uuidv4(),
      name: newFieldName.trim(),
      description: newFieldDescription.trim(),
      required: newFieldRequired,
      hidden: newFieldHidden,
      customerVisible: newFieldCustomerVisible,
      type: newFieldType,
      options:
        newFieldType === 'enum'
          ? newFieldOptions
              .split(',')
              .map((opt) => opt.trim())
              .filter((opt) => opt)
          : undefined,
    })

    setNewFieldName('')
    setNewFieldDescription('')
    setNewFieldRequired(false)
    setNewFieldHidden(false)
    setNewFieldType('text')
    setNewFieldOptions('')
    setNewFieldCustomerVisible(true)
  }

  const handleDeleteField = (index: number) => {
    confirmDialog.confirm(
      {
        title: 'Feld löschen bestätigen',
        description:
          'Möchten Sie dieses benutzerdefinierte Feld wirklich löschen? Alle gespeicherten Daten für dieses Feld gehen verloren.',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        variant: 'destructive',
      },
      () => remove(index)
    )
  }

  const handleEditField = (index: number) => {
    setEditingFieldIndex(index)
  }

  const handleSaveField = (index: number, updatedField: any) => {
    if (updatedField.type === 'enum' && !updatedField.options) return

    const isDuplicate = fields.some(
      (field, fieldIndex) =>
        fieldIndex !== index && field.name.toLowerCase() === updatedField.name.toLowerCase()
    )
    if (isDuplicate) {
      confirmDialog.confirm(
        {
          title: 'Doppelter Feldname',
          description: `Ein Feld mit dem Namen "${updatedField.name}" existiert bereits. Bitte verwenden Sie einen anderen Namen.`,
          confirmText: 'OK',
          cancelText: '',
          variant: 'default',
        },
        () => {}
      )
      return
    }

    // make sure we retain the business id that currently lives in the
    // form values; the `field` argument provided to the editing hook may not
    // include it if react-hook-form added its own internal tracking id.
    const currentFields = getValues('tenant.settings.bookingCustomFields')
    const original = currentFields?.[index] || {}
    const merged = { ...original, ...updatedField }

    update(index, merged)
    handleCancelEdit()
  }

  const handleCancelEdit = () => {
    setEditingFieldIndex(null)
  }

  return (
    <div className="space-y-10">
      {/* Tenant-Specific Settings */}
      {tenant?.type === TenantType.FOOD && (
        <section>
          <h3 className="mb-2 text-lg font-semibold">Reservierungen</h3>
          <p className="text-muted-foreground mb-4 text-sm">
            Konfigurieren Sie spezifische Einstellungen für Ihre Reservierungen wie Tischkapazitäten
            und Zeitslots.
          </p>
          <FoodTenantReservation />
        </section>
      )}
      <section>
        <h3 className="mb-2 text-lg font-semibold">Reservierungsstatus</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Wählen Sie den Standardstatus für neue Reservierungen.
        </p>
        <ServiceTenantReservation />
      </section>

      {/* Booking Thresholds Section */}
      <section>
        <h3 className="mb-2 text-lg font-semibold">Buchungsfristen</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Legen Sie fest, bis wann Reservierungen getätigt oder storniert werden können.
        </p>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <Controller
            name="tenant.settings.thresholdBooking"
            control={control}
            render={({ field }) => (
              <LabeledInputNumber
                label="Zeitlimit für neue Reservierungen"
                className="w-min"
                value={field.value}
                min={1}
                suffix="min"
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="tenant.settings.thresholdCancelation"
            control={control}
            render={({ field }) => (
              <LabeledInputNumber
                label="Zeitlimit für Stornierungen"
                className="w-min"
                value={field.value}
                min={1}
                suffix="min"
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </section>

      {/* Terms and Conditions Section */}
      <section>
        <h3 className="mb-2 text-lg font-semibold">Bedingungen</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Definieren Sie die Reservierungs- und Stornobedingungen für Ihre Kunden.
        </p>
        <div className="space-y-6">
          <Controller
            name="tenant.settings.termsForReservation"
            control={control}
            render={({ field }) => (
              <FloatingLabel label={`Reservierungsbedingungen (${field.value?.length || 0}/200)`}>
                <Textarea {...field} maxLength={200} rows={5} className="resize-none" />
              </FloatingLabel>
            )}
          />
          <Controller
            name="tenant.settings.cancelationNote"
            control={control}
            render={({ field }) => (
              <FloatingLabel label={`Stornobedingungen (${field.value?.length || 0}/200)`}>
                <Textarea {...field} maxLength={200} rows={5} className="resize-none" />
              </FloatingLabel>
            )}
          />
          <TenantCustomCondition />
        </div>
      </section>

      {/* Required Fields Section */}
      <section>
        <h3 className="mb-2 text-lg font-semibold">Pflichtfelder</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Legen Sie fest, welche Felder bei der Reservierung ausgefüllt werden müssen.
        </p>
        <div className="space-y-4">
          <Controller
            name="tenant.settings.requirePhone"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="Telefon als Pflichtfeld"
                onValueChange={field.onChange}
              />
            )}
          />
          <Controller
            name="tenant.settings.requireEmail"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={field.value ?? false}
                name={field.name}
                ref={field.ref}
                label="E-Mail als Pflichtfeld"
                onValueChange={field.onChange}
              />
            )}
          />
          {hasCustomerAddress && (
            <Controller
              name="tenant.settings.requireAddress"
              control={control}
              render={({ field }) => (
                <LabeledInputSwitch
                  checked={!!field.value}
                  name={field.name}
                  ref={field.ref}
                  label="Adresse als Pflichtfeld"
                  onValueChange={field.onChange}
                />
              )}
            />
          )}
          <Controller
            name="tenant.settings.requireAdditionalInfo"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                checked={!!field.value}
                name={field.name}
                ref={field.ref}
                label="Zusatzinformationen als Pflichtfeld"
                onValueChange={field.onChange}
              />
            )}
          />
        </div>
      </section>

      {/* Custom Fields Section */}
      <section>
        <h3 className="mb-2 text-lg font-semibold">Benutzerdefinierte Felder</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Definieren Sie zusätzliche Felder, die Kunden bei der Buchung ausfüllen müssen.
        </p>
        <div className="space-y-4">
          {/* Add new field form */}
          <div className="rounded-lg border p-4">
            <Label className="text-sm font-medium">Neues Feld hinzufügen</Label>
            <div className="mt-3 space-y-3">
              <div>
                <Label htmlFor="fieldName" className="text-sm">
                  Feldname
                </Label>
                <Input
                  id="fieldName"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="z.B. Allergien, Sonderwünsche"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="fieldDescription" className="text-sm">
                  Beschreibung
                </Label>
                <Textarea
                  id="fieldDescription"
                  value={newFieldDescription}
                  onChange={(e) => setNewFieldDescription(e.target.value)}
                  placeholder="Beschreibung für das Feld"
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="fieldType" className="text-sm">
                  Feldtyp
                </Label>
                <Select
                  value={newFieldType}
                  onValueChange={(value) => setNewFieldType(value as BookingCustomFieldType)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Nummer</SelectItem>
                    <SelectItem value="boolean">Ja/Nein</SelectItem>
                    <SelectItem value="enum">Auswahl (Dropdown)</SelectItem>
                    <SelectItem value="date">Datum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newFieldType === 'enum' && (
                <div>
                  <Label htmlFor="fieldOptions" className="text-sm">
                    Auswahloptionen (durch Komma getrennt)
                  </Label>
                  <Input
                    id="fieldOptions"
                    value={newFieldOptions}
                    onChange={(e) => setNewFieldOptions(e.target.value)}
                    placeholder="z.B. Option 1, Option 2, Option 3"
                    className="mt-1"
                  />
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Switch
                  id="fieldRequired"
                  checked={newFieldRequired}
                  onCheckedChange={setNewFieldRequired}
                />
                <Label htmlFor="fieldRequired" className="text-sm">
                  Pflichtfeld
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="fieldRequired"
                  checked={newFieldHidden}
                  onCheckedChange={setNewFieldHidden}
                />
                <Label htmlFor="fieldRequired" className="text-sm">
                  Versteckt
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="fieldCustomerVisible"
                  checked={newFieldCustomerVisible}
                  onCheckedChange={setNewFieldCustomerVisible}
                />
                <Label htmlFor="fieldCustomerVisible" className="text-sm">
                  Im Kundenbereich anzeigen
                </Label>
              </div>
              <Button
                onClick={handleAddField}
                disabled={
                  !newFieldName.trim() ||
                  !newFieldDescription.trim() ||
                  (newFieldType === 'enum' && !newFieldOptions.trim())
                }
                className="w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Feld hinzufügen
              </Button>
            </div>
          </div>

          {/* Existing fields */}
          {fields.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Vorhandene Felder</Label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  // sortables use the internal id, not the business id
                  items={fields.map((field) => (field as any).fieldArrayId)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field, index) => {
                    const arrayId = (field as any).fieldArrayId
                    return (
                      <SortableCustomFieldContainer
                        key={arrayId}
                        field={field}
                        index={index}
                        isEditing={editingFieldIndex === index}
                        onEditStart={handleEditField}
                        onEditCancel={handleCancelEdit}
                        onSave={handleSaveField}
                        onDelete={handleDeleteField}
                      >
                        {({
                          field: currentField,
                          index: currentIndex,
                          isEditing: isCurrentlyEditing,
                          editingState,
                        }) => {
                          const fieldInfo = {
                            // `id` here is only used by the sortable hook; it needs
                            // to match the value stored in `items` above
                            id: arrayId,
                            field: currentField,
                            index: currentIndex,
                            isEditing: isCurrentlyEditing,
                          }

                          const groupedEditingState = {
                            name: editingState.editingFieldName,
                            setName: editingState.setEditingFieldName,
                            description: editingState.editingFieldDescription,
                            setDescription: editingState.setEditingFieldDescription,
                            required: editingState.editingFieldRequired,
                            setRequired: editingState.setEditingFieldRequired,
                            hidden: editingState.editingFieldHidden,
                            setHidden: editingState.setEditingFieldHidden,
                            customerVisible: editingState.editingFieldCustomerVisible,
                            setCustomerVisible: editingState.setEditingFieldCustomerVisible,
                            type: editingState.editingFieldType,
                            setType: editingState.setEditingFieldType,
                            options: editingState.editingFieldOptions,
                            setOptions: editingState.setEditingFieldOptions,
                            canSave: editingState.canSave,
                          }

                          const fieldActions = {
                            onEdit: editingState.handleEdit,
                            onDelete: editingState.handleDelete,
                            onSave: editingState.handleSave,
                            onCancel: editingState.handleCancel,
                          }

                          return (
                            <SortableCustomField
                              fieldInfo={fieldInfo}
                              editingState={groupedEditingState}
                              fieldActions={fieldActions}
                            />
                          )
                        }}
                      </SortableCustomFieldContainer>
                    )
                  })}
                </SortableContext>
              </DndContext>
            </div>
          )}

          {fields.length === 0 && (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-muted-foreground">
                Noch keine benutzerdefinierten Felder definiert.
              </p>
            </div>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={confirmDialog.isOpen}
        onOpenChange={confirmDialog.setIsOpen}
        title={confirmDialog.options.title}
        description={confirmDialog.options.description}
        confirmText={confirmDialog.options.confirmText}
        cancelText={confirmDialog.options.cancelText}
        variant={confirmDialog.options.variant}
        onConfirm={confirmDialog.handleConfirm}
      />
    </div>
  )
}

export default TenantReservation
